import { useCallback, useRef, useState } from 'react'
import { KeyboardSensor, PointerSensor, useSensors, useSensor } from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import type { WorkIssue } from '@/types/github'

/**
 * Shared drag plumbing for the kanban board and the My-Issues priority list.
 * Enter opens the card; Space alone starts a keyboard drag (so the two don't
 * collide). The post-drag guard swallows the click that a pointer drag's
 * final pointerup synthesises on the source card, so a drop never also opens
 * the Sheet.
 */
export function useDragHarness(onOpen: (issue: WorkIssue) => void) {
  const [active, setActive] = useState<WorkIssue | null>(null)
  const dragEndedAt = useRef(0)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space'] },
    }),
  )

  const openGuarded = useCallback(
    (issue: WorkIssue) => {
      if (Date.now() - dragEndedAt.current < 250) return
      onOpen(issue)
    },
    [onOpen],
  )

  const markDragEnded = useCallback(() => {
    dragEndedAt.current = Date.now()
  }, [])

  return { active, setActive, sensors, openGuarded, markDragEnded }
}
